"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { Button } from "@/components/ui/button";
import { generateSlug } from "@/lib/utils";
import { useSubscriptionLimits } from "@/components/SubscriptionGuard";
import { useMailchimpAudiences } from "@/hooks/useMailchimpAudiences";
import { useWhitelabelVisibility } from "@/hooks/useWhitelabelVisibility";
import { clientLogger } from "@/lib/client-logger";
import type { TemplateInfo, CampaignPrerequisiteConfig } from "@/lib/claims/eligibility-types";
import {
  StepProgress,
  CampaignTypeStep,
  CampaignDetailsStep,
  DigitalAssetsStep,
  IntegrationsStep,
  LocationsStep,
  ReviewStep,
  type CampaignFormData,
  type ClueFormData,
  type AtomicTemplateOption,
  type TenantColors,
} from "./_components";

interface ApiCampaignResponse {
  id: string;
  status: string;
  title: string;
  slug: string;
}

interface ApiTemplateResponse {
  id: string;
  name: string;
  onChainTemplateId: string | null;
  status: "PENDING" | "CREATING" | "CREATED" | "FAILED";
  immutableData?: {
    img?: string;
    video?: string;
  };
}

const STEPS = [
  { number: 1, title: "Campaign Type" },
  { number: 2, title: "Campaign Details" },
  { number: 3, title: "Digital Assets" },
  { number: 4, title: "Locations" },
  { number: 5, title: "Integrations" },
  { number: 6, title: "Review" },
];

export default function NewCampaignPage() {
  const { data: session } = useSession();
  const router = useRouter();
  const { checkLimit, limits } = useSubscriptionLimits();
  const { audiences, isLoading: audiencesLoading, error: audiencesError, refetch: refetchAudiences } = useMailchimpAudiences();
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const initialStep = parseInt(searchParams.get('step') || '1');
  const [currentStep, setCurrentStep] = useState(initialStep);

  // Get whitelabel visibility for button styling
  const userRole = session?.user?.tenants?.[0]?.role;
  const { isWhitelabelMode } = useWhitelabelVisibility(userRole);

  // Tenant colors for whitelabel button styling
  const [tenantColors, setTenantColors] = useState<TenantColors | null>(null);

  // Fetch tenant colors when in whitelabel mode
  useEffect(() => {
    const fetchTenantColors = async () => {
      if (!isWhitelabelMode || !session?.user) return;
      try {
        const response = await fetch("/api/settings");
        if (response.ok) {
          const data = await response.json();
          if (data.tenant) {
            setTenantColors({
              primaryColor: data.tenant.primaryColor || "#3B82F6",
              secondaryColor: data.tenant.secondaryColor || "#1E40AF",
              accentColor: data.tenant.accentColor || "#10B981",
            });
          }
        }
      } catch (error) {
        clientLogger.error("Error fetching tenant colors", {
          error: error instanceof Error ? error.message : "Unknown error"
        });
      }
    };
    fetchTenantColors();
  }, [isWhitelabelMode, session]);

  // Button styles for whitelabel mode (inline styles like preview panel)
  const primaryButtonStyle = isWhitelabelMode && tenantColors
    ? { backgroundColor: tenantColors.primaryColor, borderColor: tenantColors.primaryColor, color: '#FFFFFF' }
    : undefined;
  const secondaryButtonStyle = isWhitelabelMode && tenantColors
    ? { backgroundColor: tenantColors.secondaryColor, borderColor: tenantColors.secondaryColor, color: '#FFFFFF' }
    : undefined;
  const accentButtonStyle = isWhitelabelMode && tenantColors
    ? { backgroundColor: tenantColors.accentColor, borderColor: tenantColors.accentColor, color: '#FFFFFF' }
    : undefined;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentCampaignCount, setCurrentCampaignCount] = useState(0);
  const [atomicTemplates, setAtomicTemplates] = useState<AtomicTemplateOption[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [savedCampaignId, setSavedCampaignId] = useState<string | null>(null);
  const [schemaCreationStatus, setSchemaCreationStatus] = useState<{
    inProgress: boolean;
    success?: boolean;
    error?: string;
  }>({ inProgress: false });

  // Handle successful return from Stripe checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const successParam = urlParams.get('success');
    const planParam = urlParams.get('plan');

    if (successParam === 'true' && planParam) {
      setSuccess(`Successfully upgraded to ${planParam} plan! You can now create campaigns.`);

      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);

      setTimeout(() => setSuccess(""), 5000);
    }
  }, []);

  // Load atomic templates for the tenant
  useEffect(() => {
    const loadAtomicTemplates = async () => {
      if (!session?.user) return;

      setTemplatesLoading(true);
      try {
        const response = await fetch('/api/digital-assets/templates');
        if (response.ok) {
          const data = await response.json();
          const convertCidToUrl = (cid?: string): string | undefined => {
            if (!cid) return undefined;
            if (cid.startsWith('ipfs://')) {
              return cid.replace('ipfs://', 'https://gateway.pinata.cloud/ipfs/');
            }
            if (cid.startsWith('Qm') || cid.startsWith('baf')) {
              return `https://gateway.pinata.cloud/ipfs/${cid}`;
            }
            return cid;
          };

          setAtomicTemplates(data.templates?.map((t: ApiTemplateResponse) => ({
            id: t.id,
            name: t.name,
            onChainTemplateId: t.onChainTemplateId,
            status: t.status,
            imageUrl: convertCidToUrl(t.immutableData?.img),
            videoUrl: convertCidToUrl(t.immutableData?.video),
          })) || []);
        }
      } catch (error) {
        clientLogger.error("Error loading atomic templates", {
          error: error instanceof Error ? error.message : "Unknown error"
        });
      } finally {
        setTemplatesLoading(false);
      }
    };

    loadAtomicTemplates();
  }, [session]);

  const [campaignData, setCampaignData] = useState<CampaignFormData>({
    campaignType: "" as CampaignFormData["campaignType"],
    title: "",
    headline: "",
    description: "",
    categories: [],
    coverImageUrl: "",
    orgDescription: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    startAt: "",
    endAt: "",
    timezone: "America/New_York",
    participantCap: "",
    mintCap: "",
    isPublic: true,
    showMap: true,
    mailchimpAudienceId: "",
    chain: "WAX",
    authMode: "PLATFORM",
  });

  const [clues, setClues] = useState<ClueFormData[]>([
    {
      title: "",
      description: "",
      address: "",
      latitude: undefined,
      longitude: undefined,
      radiusMiles: null,
      hideAddress: false,
      showMap: true,
      slug: "",
      orderIndex: 0,
      iykChipId: "",
      imageUrl: ""
    }
  ]);

  // Check campaign limits on load
  useEffect(() => {
    const checkCampaignLimits = async () => {
      try {
        const response = await fetch("/api/campaigns");
        if (response.ok) {
          const data = await response.json();
          const activeCampaigns = data.campaigns?.filter((c: ApiCampaignResponse) =>
            c.status === 'PUBLISHED' || c.status === 'ACTIVE'
          ).length || 0;
          setCurrentCampaignCount(activeCampaigns);

          if (limits && limits.activeCampaigns !== null && limits.activeCampaigns !== -1) {
            if (activeCampaigns >= limits.activeCampaigns) {
              router.push(`/dashboard/upgrade?message=${encodeURIComponent('You have reached your campaign limit. Upgrade to create more campaigns.')}&current=${activeCampaigns}&limit=${limits.activeCampaigns}&returnUrl=/dashboard/campaigns/new`);
            }
          }
        }
      } catch (error) {
        clientLogger.error("Error checking campaign limits", { error: error instanceof Error ? error.message : "Unknown error" });
      }
    };

    if (session?.user && limits) {
      checkCampaignLimits();
    }
  }, [session, limits, router]);

  const handleCampaignChange = (field: keyof CampaignFormData, value: string | boolean) => {
    setCampaignData(prev => ({ ...prev, [field]: value }));
  };

  const handleCategoriesChange = (categories: CampaignFormData["categories"]) => {
    setCampaignData(prev => ({ ...prev, categories }));
  };

  const handlePrerequisitesChange = (config: CampaignPrerequisiteConfig) => {
    setCampaignData(prev => ({ ...prev, prerequisites: config }));
  };

  // Convert atomic templates to TemplateInfo format for CampaignPrerequisites
  const availablePrerequisiteTemplates: TemplateInfo[] = atomicTemplates
    .filter(t => t.onChainTemplateId !== null && t.status === "CREATED")
    .map(t => ({
      templateId: t.onChainTemplateId as string,
      name: t.name,
      imageUrl: t.imageUrl,
      collectionName: "campaign",
    }));

  const buildLocationText = () => {
    const { streetAddress, city, state, zipCode } = campaignData;
    const parts: string[] = [];
    if (streetAddress) parts.push(streetAddress);
    if (city && state) {
      parts.push(`${city}, ${state}`);
    } else if (city || state) {
      parts.push(city || state);
    }
    if (zipCode) parts.push(zipCode);
    return parts.join(', ');
  };

  const buildRequestData = (overrides: Record<string, unknown>) => {
    const { city: _city, state: _state, streetAddress: _streetAddress, zipCode: _zipCode, campaignType: _campaignType, ...rest } = campaignData;
    return {
      ...rest,
      campaignType: campaignData.campaignType || 'MULTI_LOCATION',
      locationText: buildLocationText(),
      ...overrides,
    };
  };

  const mapClueForSave = (clue: ClueFormData, includeId = false) => {
    const isSingle = campaignData.campaignType === 'SINGLE_LOCATION';
    return {
      ...(includeId && clue.id ? { id: clue.id } : {}),
      title: isSingle ? (campaignData.title || clue.title || "Event") : (clue.title || "Location"),
      description: isSingle ? (campaignData.description || clue.description || "") : (clue.description || "Description pending"),
      address: isSingle ? (campaignData.streetAddress || clue.address || "") : (clue.address || ""),
      latitude: isSingle ? (campaignData.latitude || clue.latitude) : clue.latitude,
      longitude: isSingle ? (campaignData.longitude || clue.longitude) : clue.longitude,
      radiusMiles: isSingle ? null : clue.radiusMiles,
      hideAddress: isSingle ? false : clue.hideAddress,
      showMap: isSingle ? campaignData.showMap : (clue.showMap ?? true),
      slug: clue.slug || generateSlug(isSingle ? (campaignData.title || "event") : (clue.title || "location")),
      orderIndex: clue.orderIndex,
      iykChipId: clue.iykChipId,
      atomicTemplateId: clue.atomicTemplateId || null,
      poapEventId: clue.poapEventId || null,
      imageUrl: clue.imageUrl || null,
    };
  };

  const handleClueChange = (index: number, field: keyof ClueFormData, value: string | number | boolean | string[]) => {
    setClues(prev => prev.map((clue, i) =>
      i === index
        ? {
            ...clue,
            [field]: value,
            ...(field === 'title' && typeof value === 'string' && {
              slug: generateSlug(value)
            })
          }
        : clue
    ));
  };

  // Save as draft and redirect to edit page to enable template creation
  // Currently unused but kept for future use
  const _handleSaveAsDraftAndRedirect = async () => {
    if (!campaignData.title || !campaignData.description) {
      setError("Title and description are required to save as draft");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const formatDateForBackend = (dateTimeLocal: string): string | null => {
        if (!dateTimeLocal) return null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateTimeLocal)) {
          return new Date(dateTimeLocal + 'T00:00:00').toISOString();
        }
        const date = new Date(dateTimeLocal);
        if (isNaN(date.getTime())) return null;
        return date.toISOString();
      };

      const requestData = buildRequestData({
        startAt: formatDateForBackend(campaignData.startAt),
        endAt: formatDateForBackend(campaignData.endAt),
        participantCap: campaignData.participantCap && campaignData.participantCap.trim() !== "" ? parseInt(campaignData.participantCap) : null,
        mintCap: campaignData.mintCap && campaignData.mintCap.trim() !== "" ? parseInt(campaignData.mintCap) : null,
        status: "DRAFT",
        clues: clues.map(clue => mapClueForSave(clue))
      });

      const response = await fetch("/api/campaigns", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to save campaign");
      }

      // Redirect to edit page where they can create templates
      router.push(`/dashboard/campaigns/${data.campaign.id}?step=2`);
    } catch (error) {
      setError(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle transition from step 1 to step 2
  // Saves campaign as draft and creates schema on blockchain
  const handleNextFromStep1 = async () => {
    setIsLoading(true);
    setError("");
    setSchemaCreationStatus({ inProgress: true });

    try {
      // Step 1: Save campaign as draft (create or update)
      let campaignId = savedCampaignId;

      const formatDateForBackend = (dateTimeLocal: string): string | null => {
        if (!dateTimeLocal) return null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateTimeLocal)) {
          return new Date(dateTimeLocal + 'T00:00:00').toISOString();
        }
        const date = new Date(dateTimeLocal);
        if (isNaN(date.getTime())) return null;
        return date.toISOString();
      };

      const clueData = clues.map(clue => mapClueForSave(clue, true));

      const requestData = buildRequestData({
        startAt: formatDateForBackend(campaignData.startAt),
        endAt: formatDateForBackend(campaignData.endAt),
        participantCap: campaignData.participantCap && campaignData.participantCap.trim() !== "" ? parseInt(campaignData.participantCap) : null,
        mintCap: campaignData.mintCap && campaignData.mintCap.trim() !== "" ? parseInt(campaignData.mintCap) : null,
        status: "DRAFT",
        clues: clueData,
      });

      if (campaignId) {
        // Update existing draft
        clientLogger.info("Updating existing draft before step 2", {
          campaignId,
          title: campaignData.title,
        });

        const campaignResponse = await fetch(`/api/campaigns/${campaignId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        });

        if (!campaignResponse.ok) {
          const campaignResponseData = await campaignResponse.json();
          throw new Error(campaignResponseData.error || "Failed to update campaign");
        }

        clientLogger.info("Draft updated", { campaignId });
      } else {
        // Create new draft
        clientLogger.info("Saving campaign as draft before step 2", {
          title: campaignData.title,
          clueCount: clues.length,
        });

        const campaignResponse = await fetch("/api/campaigns", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        });

        const campaignResponseData = await campaignResponse.json();

        if (!campaignResponse.ok) {
          throw new Error(campaignResponseData.error || "Failed to save campaign");
        }

        if (!campaignResponseData.campaign?.id) {
          throw new Error("Campaign was created but no ID was returned");
        }

        campaignId = campaignResponseData.campaign.id;
        setSavedCampaignId(campaignId);

        // Save clue IDs back into state so subsequent PATCHes update rather than create
        if (campaignResponseData.clues && Array.isArray(campaignResponseData.clues)) {
          setClues(prev => prev.map((clue, i) => ({
            ...clue,
            id: campaignResponseData.clues[i]?.id || clue.id,
          })));
        }

        clientLogger.info("Campaign saved as draft", { campaignId });
      }

      // Step 2: Create schema for the campaign (WAX only - POAP doesn't need schemas)
      if (campaignData.chain !== 'POAP') {
        clientLogger.info("Creating schema for campaign", { campaignId });

        const schemaResponse = await fetch("/api/digital-assets/schemas", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: campaignData.title,
            campaignId: campaignId,
          }),
        });

        const schemaData = await schemaResponse.json();
        clientLogger.info("Schema API response", {
          status: schemaResponse.status,
          existing: schemaData.existing,
          schemaId: schemaData.schema?.id,
          blockchainSuccess: schemaData.blockchain?.success,
        });

        if (schemaResponse.status === 409) {
          // Schema with same name already exists - that's fine, continue
          clientLogger.info("Schema already exists for campaign (409)", { campaignId });
          setSchemaCreationStatus({ inProgress: false, success: true });
        } else if (!schemaResponse.ok) {
          throw new Error(schemaData.error || "Failed to create schema");
        } else {
          // Schema created or returned existing
          const isExisting = schemaData.existing === true;
          const blockchainSuccess = schemaData.blockchain?.success;

          setSchemaCreationStatus({
            inProgress: false,
            success: blockchainSuccess || isExisting,
            error: blockchainSuccess ? undefined : schemaData.blockchain?.error,
          });

          if (isExisting) {
            clientLogger.info("Using existing schema for campaign", {
              campaignId,
              schemaId: schemaData.schema?.id,
            });
            setSuccess("Campaign saved!");
            setTimeout(() => setSuccess(""), 3000);
          } else if (blockchainSuccess) {
            setSuccess("Campaign saved and schema created on blockchain!");
            setTimeout(() => setSuccess(""), 3000);
          } else if (schemaData.blockchain?.error) {
            // Schema saved but blockchain failed - show warning but continue
            clientLogger.warn("Schema saved but blockchain creation pending", {
              error: schemaData.blockchain.error,
            });
          }
        }
      } else {
        // POAP chain - skip schema creation
        clientLogger.info("Skipping schema creation for POAP campaign", { campaignId });
        setSchemaCreationStatus({ inProgress: false, success: true });
        setSuccess("Campaign saved!");
        setTimeout(() => setSuccess(""), 3000);
      }

      // Continue to step 4 (Locations)
      setCurrentStep(4);

    } catch (error) {
      clientLogger.error("Error in step 1 to step 2 transition", {
        error: error instanceof Error ? error.message : "Unknown error"
      });
      setError(error instanceof Error ? error.message : "An error occurred");
      setSchemaCreationStatus({ inProgress: false, error: error instanceof Error ? error.message : "An error occurred" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLocationChange = (index: number, locationData: {
    address: string;
    latitude?: number;
    longitude?: number;
    radiusMiles?: number | null;
    hideAddress?: boolean;
  }) => {
    setClues(prev => {
      const updatedClues = [...prev];
      updatedClues[index] = {
        ...updatedClues[index],
        address: locationData.address,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        radiusMiles: locationData.radiusMiles,
        hideAddress: locationData.hideAddress
      };
      return updatedClues;
    });
  };

  const addClue = () => {
    setClues(prev => [...prev, {
      title: "",
      description: "",
      address: "",
      latitude: undefined,
      longitude: undefined,
      radiusMiles: null,
      hideAddress: false,
      showMap: true,
      slug: "",
      orderIndex: prev.length,
      iykChipId: "",
      imageUrl: ""
    }]);
  };

  const removeClue = (index: number) => {
    if (clues.length > 1) {
      setClues(prev => prev.filter((_, i) => i !== index).map((clue, i) => ({ ...clue, orderIndex: i, iykChipId: clue.iykChipId })));
    }
  };

  const handleSubmit = async (asDraft = false) => {
    if (!asDraft) {
      if (!campaignData.title || !campaignData.description) {
        setError("Title and description are required");
        return;
      }

      const requireAddress = campaignData.campaignType === 'MULTI_LOCATION';
      if (clues.some(clue => !clue.title || !clue.description || (requireAddress && !clue.address))) {
        setError(requireAddress
          ? "All clues must have title, description, and address"
          : "All clues must have title and description");
        return;
      }
    }

    if (!asDraft) {
      const limitCheck = await checkLimit('create_campaign', currentCampaignCount);
      if (!limitCheck.allowed) {
        setError(`Cannot create campaign: ${limitCheck.reason}`);
        if (limitCheck.upgradeRequired) {
          setTimeout(() => {
            router.push(`/dashboard/upgrade?plan=${limitCheck.upgradeRequired}&returnUrl=/dashboard/campaigns/new`);
          }, 2000);
        }
        return;
      }
    }

    setIsLoading(true);
    setError("");

    try {
      clientLogger.debug("Saving campaign dates", {
        startAt: campaignData.startAt,
        endAt: campaignData.endAt
      });

      const formatDateForBackend = (dateTimeLocal: string): string | null => {
        if (!dateTimeLocal) {
          clientLogger.debug("Empty date, returning null");
          return null;
        }

        const date = new Date(dateTimeLocal);
        const isoString = date.toISOString();

        clientLogger.debug("Formatted date for backend", { input: dateTimeLocal, output: isoString });
        return isoString;
      };

      const requestData = buildRequestData({
        startAt: formatDateForBackend(campaignData.startAt),
        endAt: formatDateForBackend(campaignData.endAt),
        participantCap: campaignData.participantCap && campaignData.participantCap.trim() !== "" ? parseInt(campaignData.participantCap) : null,
        mintCap: campaignData.mintCap && campaignData.mintCap.trim() !== "" ? parseInt(campaignData.mintCap) : null,
        status: asDraft ? "DRAFT" : "PUBLISHED",
        clues: clues.map(clue => mapClueForSave(clue, true))
      });

      clientLogger.info("Sending campaign data", { savedCampaignId, requestData });

      let response: Response;
      if (savedCampaignId) {
        // Update the existing draft
        response = await fetch(`/api/campaigns/${savedCampaignId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        });
      } else {
        // Create new campaign
        response = await fetch("/api/campaigns", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        });
      }

      const data = await response.json();
      clientLogger.info("Campaign API response", { status: response.status, data });

      if (response.status === 402 && data.requiresUpgrade) {
        if (!asDraft) {
          await handleSubmit(true);
        }

        const upgradeUrl = new URL("/dashboard/upgrade", window.location.origin);
        upgradeUrl.searchParams.set("limitType", data.limitType);
        upgradeUrl.searchParams.set("message", encodeURIComponent(data.message));
        if (data.current) upgradeUrl.searchParams.set("current", data.current.toString());
        if (data.limit) upgradeUrl.searchParams.set("limit", data.limit.toString());
        upgradeUrl.searchParams.set("returnUrl", "/dashboard/campaigns/new?step=6");

        router.push(upgradeUrl.toString());
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || (savedCampaignId ? "Failed to update campaign" : "Failed to create campaign"));
      }

      router.push("/dashboard/campaigns");
    } catch (error) {
      setError(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  if (!session) return null;

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Create New Campaign</h1>

        <StepProgress steps={STEPS} currentStep={currentStep} tenantColors={isWhitelabelMode ? tenantColors : null} />

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-green-800">{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="bg-white shadow rounded-lg p-6">
          {currentStep === 1 && (
            <CampaignTypeStep
              onSelect={(type) => {
                setCampaignData(prev => ({ ...prev, campaignType: type }));
                setCurrentStep(2);
              }}
              tenantColors={isWhitelabelMode ? tenantColors : null}
            />
          )}

          {currentStep === 2 && (
            <CampaignDetailsStep
              campaignData={campaignData}
              onCampaignChange={handleCampaignChange}
              onCategoriesChange={handleCategoriesChange}
              onLocationChange={(locationData) => {
                setCampaignData(prev => ({
                  ...prev,
                  streetAddress: locationData.address,
                  city: "",
                  state: "",
                  zipCode: "",
                  latitude: locationData.latitude,
                  longitude: locationData.longitude,
                }));
              }}
              tenantColors={isWhitelabelMode ? tenantColors : null}
            />
          )}

          {currentStep === 3 && (
            <DigitalAssetsStep
              campaignData={campaignData}
              onCampaignChange={handleCampaignChange}
              onPrerequisitesChange={handlePrerequisitesChange}
              availablePrerequisiteTemplates={availablePrerequisiteTemplates}
              templatesLoading={templatesLoading}
              tenantColors={isWhitelabelMode ? tenantColors : null}
            />
          )}

          {currentStep === 4 && (
            <LocationsStep
              clues={clues}
              campaignType={campaignData.campaignType}
              onClueChange={handleClueChange}
              onLocationChange={handleLocationChange}
              onAddClue={addClue}
              onRemoveClue={removeClue}
              atomicTemplates={atomicTemplates}
              templatesLoading={templatesLoading}
              campaignId={savedCampaignId}
              campaignPrerequisites={campaignData.prerequisites}
              onTemplateCreated={(template) => {
                setAtomicTemplates(prev => [
                  {
                    id: template.id,
                    name: template.name,
                    onChainTemplateId: template.onChainTemplateId,
                    status: template.status,
                    imageUrl: template.imageUrl || undefined,
                    videoUrl: template.videoUrl || undefined,
                  },
                  ...prev,
                ]);
              }}
              tenantColors={isWhitelabelMode ? tenantColors : null}
              chain={campaignData.chain}
            />
          )}

          {currentStep === 5 && (
            <IntegrationsStep
              campaignData={campaignData}
              onCampaignChange={handleCampaignChange}
              audiences={audiences}
              audiencesLoading={audiencesLoading}
              audiencesError={audiencesError}
              onRefetchAudiences={refetchAudiences}
              tenantColors={isWhitelabelMode ? tenantColors : null}
              campaignId={savedCampaignId}
            />
          )}

          {currentStep === 6 && (
            <ReviewStep
              campaignData={campaignData}
              clues={clues}
              atomicTemplates={atomicTemplates}
            />
          )}

          {/* Navigation — hidden on step 1 (type selection auto-advances) */}
          {currentStep > 1 && (
          <div className="flex justify-between mt-8 pt-6 border-t">
            <div className="flex space-x-3">
              <Button
                onClick={() => router.push("/dashboard/campaigns")}
                variant={secondaryButtonStyle ? undefined : "outline"}
                style={secondaryButtonStyle}
                disabled={isLoading}
              >
                Cancel
              </Button>

              <Button
                onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
                variant={accentButtonStyle ? undefined : "outline"}
                style={accentButtonStyle}
                disabled={currentStep === 1}
              >
                Previous
              </Button>

              <Button
                onClick={() => handleSubmit(true)}
                disabled={isLoading}
                variant={secondaryButtonStyle ? undefined : "outline"}
                style={secondaryButtonStyle}
              >
                {isLoading ? "Saving..." : "Save as Draft"}
              </Button>
            </div>

            {currentStep < 6 ? (
              <Button
                onClick={() => {
                  if (currentStep === 3) {
                    handleNextFromStep1();
                  } else {
                    setCurrentStep(prev => Math.min(6, prev + 1));
                  }
                }}
                style={primaryButtonStyle}
                disabled={
                  (currentStep === 3 && (isLoading || schemaCreationStatus.inProgress))
                }
              >
                {currentStep === 3 && (isLoading || schemaCreationStatus.inProgress)
                  ? (schemaCreationStatus.inProgress ? "Creating schema..." : "Saving...")
                  : "Next"
                }
              </Button>
            ) : (
              <Button
                onClick={() => handleSubmit(false)}
                style={primaryButtonStyle}
                disabled={isLoading || clues.some(clue => !clue.title || !clue.description || (campaignData.campaignType === 'MULTI_LOCATION' && !clue.address))}
              >
                {isLoading ? "Publishing..." : "Publish Campaign"}
              </Button>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
